import { useMemo, useState } from "react";
import { useCustom, useGetLocale, useTranslate } from "@refinedev/core";
import { AlertTriangle, CalendarCheck, CalendarX, CheckCircle2, Clock } from "lucide-react";
import { StatusBadge, type Column, type StatusTone } from "@fawkes/ui";

import { IndeelbareTabel } from "../components/IndeelbareTabel";
import { VestigingFilter } from "../components/VestigingFilter";
import { formatDate, formatDateTime, formatEuro } from "../i18n/format";
import { PeriodeFilter, periodeBereik } from "../components/PeriodeFilter";

/**
 * Financieel → Dagafsluitingen: per dag wie hem afsloot, wie het vierogen-akkoord gaf en wat er niet
 * klopte. Een tabblad van hetzelfde scherm als Facturen en Betalingen, dus bewust dezelfde chroom.
 *
 * <p><b>Contant en pin staan apart en worden niet opgeteld.</b> Een tekort in de kas hoort niet weg te
 * vallen achter een overschot op de pin — dat is precies het soort verschil dat je wilt zien. Om dezelfde
 * reden telt de kop dágen met een verschil en geen bedragen: of een maand per saldo op nul uitkomt zegt
 * niets over hoe vaak er iets misging.
 */

interface Regel {
  /** Leeg bij een dag die niet is afgesloten: daar hoort geen afsluiting bij. */
  id: number | null;
  dag: string;
  vestigingId: number;
  vestiging: string | null;
  status: string;
  contantGeteld: number | null;
  contantVerwacht: number | null;
  verschilContant: number | null;
  pinGeteld: number | null;
  pinVerwacht: number | null;
  verschilPin: number | null;
  verschilTotaal: number | null;
  afgeslotenDoor: string | null;
  afgeslotenOp: string | null;
  akkoordNaam: string | null;
  akkoordOp: string | null;
  opmerking: string | null;
  heeftPinBijlage: boolean;
  /** Het aantal kasboekingen van die dag. */
  boekingen: number;
}

interface Antwoord {
  regels: Regel[];
  totalen: {
    dagen: number;
    dagenMetVerschil: number;
    wachtOpControle: number;
    nietAfgesloten: number;
    verschilContant: number;
    verschilPin: number;
  };
}

const STATUS_TOON: Record<string, StatusTone> = {
  AKKOORD: "success",
  WACHT_OP_CONTROLE: "warning",
  CONCEPT: "neutral",
  NIET_AFGESLOTEN: "danger",
};

/** De drie manieren waarop je naar dit scherm kijkt. */
type Blik = "alles" | "nietAfgesloten" | "verschil" | "wacht";

/** Een verschil van nul is goed nieuws en krijgt geen kleur; elk ander bedrag wel. */
function verschilToon(bedrag: number): StatusTone {
  return bedrag === 0 ? "neutral" : "danger";
}

/** Een bedrag rechts uitgelijnd met cijfers van gelijke breedte; leeg blijft een streepje. */
function Bedrag({ waarde, locale }: { waarde: number | null; locale: string }) {
  if (waarde == null) return <span className="fawkes-dagaf__leeg">—</span>;
  return <span className="tabular">{formatEuro(waarde, locale)}</span>;
}

export function DagafsluitingOverzicht() {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [periode, setPeriode] = useState("maand");
  const [eigenPeriode, setEigenPeriode] = useState({ van: "", tot: "" });
  const [vestigingen, setVestigingen] = useState<string[]>([]);
  const [blik, setBlik] = useState<Blik>("alles");

  const bereik = useMemo(() => periodeBereik(periode, new Date(), eigenPeriode), [periode, eigenPeriode]);
  const { data, isLoading, isError } = useCustom<Antwoord>({
    url: "dagafsluiting/overzicht",
    method: "get",
    config: { query: bereik.from ? { van: bereik.from, tot: bereik.to } : {} },
  });

  const alles = useMemo(() => data?.data?.regels ?? [], [data]);
  const totalen = data?.data?.totalen;

  // De vestigingen komen uit de regels zelf: dat zijn precies de vestigingen die in deze periode een
  // kas voerden, en dus de enige waarvoor een keuze betekenis heeft.
  const alleVestigingen = useMemo(() => {
    const namen = new Set<string>();
    for (const r of alles) if (r.vestiging) namen.add(r.vestiging);
    return [...namen].sort((a, b) => a.localeCompare(b));
  }, [alles]);

  const regels = useMemo(() => {
    let uit = alles;
    if (vestigingen.length > 0) uit = uit.filter((r) => r.vestiging && vestigingen.includes(r.vestiging));
    if (blik === "nietAfgesloten") uit = uit.filter((r) => r.status === "NIET_AFGESLOTEN");
    if (blik === "verschil") uit = uit.filter((r) => r.verschilContant !== 0 || r.verschilPin !== 0);
    if (blik === "wacht") uit = uit.filter((r) => r.status === "WACHT_OP_CONTROLE");
    return uit;
  }, [alles, vestigingen, blik]);

  const columns: Column<Regel>[] = [
    {
      key: "dag", header: t("dagafsluitingOverzicht.col.dag"),
      cell: (r) => <span className="tabular">{formatDate(r.dag, locale)}</span>,
    },
    { key: "vestiging", header: t("dagafsluitingOverzicht.col.vestiging"), cell: (r) => r.vestiging ?? "—" },
    {
      key: "status", header: t("dagafsluitingOverzicht.col.status"),
      cell: (r) => (
        <StatusBadge tone={STATUS_TOON[r.status] ?? "neutral"}>
          {t(`dagafsluitingOverzicht.status.${r.status}`)}
        </StatusBadge>
      ),
    },
    {
      key: "afgeslotenDoor", header: t("dagafsluitingOverzicht.col.afgeslotenDoor"),
      // Bij een dag die niet is afgesloten staat hier waaróm hij opvalt: hoeveel kasboekingen er die
      // dag waren. Een streepje zou de rij even onschuldig laten lijken als een lege zondag.
      cell: (r) => (r.status === "NIET_AFGESLOTEN"
        ? (
          <span className="fawkes-dagaf__gemist">
            {t("dagafsluitingOverzicht.boekingen", { args: [String(r.boekingen)] })}
          </span>
        )
        : (r.afgeslotenDoor ?? "—")),
    },
    {
      key: "afgeslotenOp", header: t("dagafsluitingOverzicht.col.afgeslotenOp"),
      cell: (r) => (r.afgeslotenOp
        ? <span className="tabular">{formatDateTime(r.afgeslotenOp, locale)}</span>
        : <span className="fawkes-dagaf__leeg">—</span>),
    },
    {
      // Leeg betekent hier iets: niemand heeft meegekeken. Daarom een woord en geen streepje.
      key: "akkoord", header: t("dagafsluitingOverzicht.col.akkoord"),
      cell: (r) => {
        if (r.status === "NIET_AFGESLOTEN") return <span className="fawkes-dagaf__leeg">—</span>;
        return r.akkoordNaam ? (
          <span className="fawkes-dagaf__akkoord">
            <CheckCircle2 size={14} aria-hidden="true" />
            {r.akkoordNaam}
          </span>
        ) : (
          <span className="fawkes-muted">{t("dagafsluitingOverzicht.geenAkkoord")}</span>
        );
      },
    },
    {
      // Geteld bedrag en verwacht bedrag onder elkaar in één kolom: twee kolommen zouden de rij uit
      // elkaar trekken, en het verwachte bedrag is bijzaak zolang het verschil nul is.
      key: "contant", header: t("dagafsluitingOverzicht.col.contant"), align: "right",
      cell: (r) => (r.contantGeteld == null ? <span className="fawkes-dagaf__leeg">—</span> : (
        <span className="fawkes-dagaf__telling" title={t("dagafsluitingOverzicht.tellingTitel")}>
          <Bedrag waarde={r.contantGeteld} locale={locale} />
          <small className="tabular">{formatEuro(r.contantVerwacht ?? 0, locale)}</small>
        </span>
      )),
    },
    {
      key: "verschilContant", header: t("dagafsluitingOverzicht.col.verschilContant"), align: "right",
      cell: (r) => (r.verschilContant == null ? <span className="fawkes-dagaf__leeg">—</span> : (
        <StatusBadge tone={verschilToon(r.verschilContant)}>
          {formatEuro(r.verschilContant, locale)}
        </StatusBadge>
      )),
    },
    {
      key: "pin", header: t("dagafsluitingOverzicht.col.pin"), align: "right",
      cell: (r) => (r.pinGeteld == null ? <span className="fawkes-dagaf__leeg">—</span> : (
        <span className="fawkes-dagaf__telling" title={t("dagafsluitingOverzicht.tellingTitel")}>
          <Bedrag waarde={r.pinGeteld} locale={locale} />
          <small className="tabular">{formatEuro(r.pinVerwacht ?? 0, locale)}</small>
        </span>
      )),
    },
    {
      key: "verschilPin", header: t("dagafsluitingOverzicht.col.verschilPin"), align: "right",
      cell: (r) => (r.verschilPin == null ? <span className="fawkes-dagaf__leeg">—</span> : (
        <StatusBadge tone={verschilToon(r.verschilPin)}>{formatEuro(r.verschilPin, locale)}</StatusBadge>
      )),
    },
    { key: "opmerking", header: t("dagafsluitingOverzicht.col.opmerking"), cell: (r) => r.opmerking ?? "—" },
  ];

  return (
    <div className="fawkes-fin__tabpaneel">
      <header className="fawkes-fin__head">
        <span className="fawkes-fin__head-icon" aria-hidden="true"><CalendarCheck size={26} /></span>
        <div className="fawkes-fin__head-text">
          <h2>{t("dagafsluitingOverzicht.title")}</h2>
          <p>{t("dagafsluitingOverzicht.subtitle")}</p>
        </div>
        {/* De tegels zijn tegelijk het filter: je ziet een getal dat niet klopt en klikt erop. */}
        <div className="fawkes-fin__kpis">
          <Kpi icon={<CalendarCheck size={18} />} value={String(totalen?.dagen ?? 0)}
            label={t("dagafsluitingOverzicht.kpi.dagen")}
            actief={blik === "alles"} onKies={() => setBlik("alles")} />
          <Kpi icon={<CalendarX size={18} />} value={String(totalen?.nietAfgesloten ?? 0)}
            label={t("dagafsluitingOverzicht.kpi.nietAfgesloten")} danger
            actief={blik === "nietAfgesloten"} onKies={() => setBlik("nietAfgesloten")} />
          <Kpi icon={<AlertTriangle size={18} />} value={String(totalen?.dagenMetVerschil ?? 0)}
            label={t("dagafsluitingOverzicht.kpi.metVerschil")} danger
            actief={blik === "verschil"} onKies={() => setBlik("verschil")} />
          <Kpi icon={<Clock size={18} />} value={String(totalen?.wachtOpControle ?? 0)}
            label={t("dagafsluitingOverzicht.kpi.wachtOpControle")}
            actief={blik === "wacht"} onKies={() => setBlik("wacht")} />
        </div>
      </header>

      <div className="fawkes-fin__filters">
        <PeriodeFilter waarde={periode} onWaarde={setPeriode} eigen={eigenPeriode} onEigen={setEigenPeriode} />
        {alleVestigingen.length > 1 && (
          <VestigingFilter
            label={t("financieel.filter.vestiging")}
            opties={alleVestigingen}
            gekozen={vestigingen}
            onChange={setVestigingen}
          />
        )}
      </div>

      {isError && (
        <div className="asiel-tarief__notice asiel-tarief__notice--error">
          {t("dagafsluitingOverzicht.laadfout")}
        </div>
      )}

      <IndeelbareTabel<Regel>
        tabelKey="dagafsluiting-overzicht"
        columns={columns}
        rows={regels}
        getRowKey={(r) => (r.id != null ? `a${r.id}` : `x${r.vestigingId}-${r.dag}`)}
        empty={isLoading ? t("common.loading") : t("dagafsluitingOverzicht.leeg")}
        rowClassName={(r) => (r.status === "NIET_AFGESLOTEN" ? "fawkes-dagaf__rij--gemist" : undefined)}
      />
    </div>
  );
}

/**
 * Dezelfde kengetaltegel als de andere financiële tabbladen; hier lokaal omdat hij daar niet uit wordt
 * geëxporteerd. Hier is hij ook een knop: het getal dat je verontrust is meteen het filter.
 */
function Kpi({ icon, value, label, danger, actief, onKies }: {
  icon: React.ReactNode; value: string; label: string; danger?: boolean;
  actief?: boolean; onKies?: () => void;
}) {
  return (
    <button
      type="button"
      className={`fawkes-fin__kpi fawkes-fin__kpi--knop${actief ? " is-actief" : ""}`}
      aria-pressed={actief}
      onClick={onKies}
    >
      <span className={`fawkes-fin__kpi-icon${danger ? " is-danger" : ""}`} aria-hidden="true">{icon}</span>
      <span className="fawkes-fin__kpi-text">
        <strong>{value}</strong>
        <span>{label}</span>
      </span>
    </button>
  );
}
